import {
  Activity,
  Clock,
  GitCompareArrows,
  LineChart,
  TrendingUp,
} from "lucide-react";
import { lazy } from "react";

import { pointSettles } from "@/lib/market-intelligence/topology";

import type { TabRoute } from "@/lib/routes";
import type { MarketHub } from "@/types/market-intelligence";
import type { LucideIcon } from "lucide-react";
import type { ReactElement } from "react";

/**
 * Hub workspace tabs.
 *
 * Declared here rather than in the module file: the parent page imports them
 * too, and putting them in the route table creates a circular import.
 *
 * Capacity is NOT here. A capacity auction clears for a whole region, not a
 * hub — the zonal prices a hub would show are the region's table sliced one
 * row thin, and the region already has its own Capacity page. Offering it at
 * both levels implied the hub had a capacity market of its own.
 */
const HubForwardsTab = lazy(() => import("./HubForwardsTab"));
const HubIndexTab = lazy(() => import("./HubIndexTab"));

const StripsView = lazy(() => import("./forwards/StripsView"));
const CurveView = lazy(() => import("./forwards/CurveView"));
const TrendsView = lazy(() => import("./index/TrendsView"));
const HourlyView = lazy(() => import("./index/HourlyView"));
const BasisView = lazy(() => import("./index/BasisView"));

/**
 * One sub-view of a hub tab — a segment of the in-page segmented control AND
 * a row in the sidebar.
 *
 * Declared here rather than inside the tab components so the two surfaces
 * read the SAME list. When the tabs owned their own arrays the sidebar could
 * not see them, and a view added in one place would silently not appear in
 * the other.
 */
export interface HubSubView {
  /** Path segment relative to the tab (`forwards/strips`). */
  path: string;
  label: string;
  icon: LucideIcon;
  element: ReactElement;
  /**
   * Whether this view applies to a given hub. Omitted means always.
   *
   * Basis is the case that needs it: it prices a zone against its parent hub,
   * so a trading hub with no parent has nothing to compare against — and nor
   * does a zone whose parent has no settlement node (PJM's zones against
   * Western Hub, which the lake publishes curves for but not settlements).
   * Both the pill and the sidebar row drop out together because both read
   * this.
   *
   * `parent` is the hub's parent when the caller has the topology to resolve
   * it; `undefined` when it doesn't, which keeps the parent-only test.
   */
  appliesTo?: (hub: MarketHub, parent?: MarketHub) => boolean;
  /**
   * A permission the view needs beyond Market Intelligence access. Curve is
   * the case (`market_intelligence.forward_curves`, admins only by default):
   * without it the pill, the sidebar row and the route all go — the server
   * refuses the curve's data on the same key.
   */
  requiredPermission?: string;
}

export const HUB_SUB_VIEWS: Record<"forwards" | "index", readonly HubSubView[]> = {
  forwards: [
    { path: "strips", label: "Strips", icon: TrendingUp, element: <StripsView /> },
    {
      path: "curve",
      label: "Curve",
      icon: LineChart,
      element: <CurveView />,
      requiredPermission: "market_intelligence.forward_curves",
    },
  ],
  index: [
    { path: "trends", label: "Trends", icon: Activity, element: <TrendsView /> },
    {
      path: "hourly",
      label: "Hourly",
      icon: Clock,
      element: <HourlyView />,
      // Gas settles one spot assessment a day — there is no intraday index to
      // show. Legacy PowerUp offered the view anyway and errored on it; the
      // business confirmed it does not exist for gas, so the pill and the
      // route go rather than an empty screen.
      appliesTo: (hub) => hub.commodity !== "gas",
    },
    {
      path: "basis",
      label: "Basis",
      icon: GitCompareArrows,
      element: <BasisView />,
      // A gas point's parent is Henry Hub, which settles through the gas
      // feed rather than at an ISO node — `pointSettles` asks both alike.
      appliesTo: (hub, parent) =>
        hub.parentHub !== null && (parent === undefined || pointSettles(parent)),
    },
  ],
};

/**
 * The sub-views that apply to one hub under one tab — and, given the caller's
 * permissions, that they may see.
 *
 * `permissions` is optional because the sidebar builds its rows outside any
 * component: it carries each view's `requiredPermission` onto the row instead,
 * and the nav filters it there. The in-page segmented control passes them, and
 * a view it drops is gone from its routes too, so a direct link falls through
 * to the first view it may see.
 */
export function subViewsFor(
  tab: "forwards" | "index",
  hub: MarketHub,
  permissions?: { granted: string[]; is_admin: boolean },
  /** The hub's parent, when the caller can resolve it from the topology —
   *  Basis needs it to know the parent has settlements to compare against. */
  parent?: MarketHub,
): readonly HubSubView[] {
  return HUB_SUB_VIEWS[tab].filter(
    (v) =>
      (v.appliesTo?.(hub, parent) ?? true) &&
      (!permissions ||
        !v.requiredPermission ||
        permissions.is_admin ||
        permissions.granted.includes(v.requiredPermission)),
  );
}

/**
 * Whether a tab has anything to show for one hub.
 *
 * Both tabs read a different feed, and a point need not have both. A
 * settlement-only zone (CAISO's DLAPs, ERCOT's load zones) has no forward
 * curve, so every surface under Forwards — Strips and Curve alike — can only
 * draw empty; one hub has the mirror problem, a curve and no settlement node.
 * Offering the tab anyway is the same mistake `appliesTo` already avoids for
 * Basis: a tab that exists but can never answer reads as broken rather than as
 * inapplicable.
 *
 * Filtered here rather than by adding `appliesTo` to `TabRoute`, which is a
 * shared type the whole app uses — this rule belongs to this module.
 */
export function tabAppliesTo(tab: "forwards" | "index", hub: MarketHub): boolean {
  if (tab === "forwards") return hub.curveLocation !== null;
  // Power settles at an ISO node; gas settles at its own location, through a
  // different feed entirely (`market.settled_gas_index`), which the server
  // carries as the gas point's `indexNode`. See `pointSettles`.
  return pointSettles(hub);
}

/** The tabs that apply to one hub. */
export function hubTabsFor(hub: MarketHub): ReadonlyArray<TabRoute> {
  return HUB_TAB_ROUTES.filter((t) =>
    tabAppliesTo(t.path as "forwards" | "index", hub),
  );
}

/**
 * Both tabs are `nested`: each owns a second level of views (Strips/Curve,
 * Trends/Hourly/Basis) which it routes itself via `SubTabs`. Without the flag
 * the router registers a bare `forwards` path and a deep link to
 * `forwards/curve` would not match.
 */
export const HUB_TAB_ROUTES: ReadonlyArray<TabRoute> = [
  {
    path: "forwards",
    label: "Forwards",
    icon: TrendingUp,
    element: <HubForwardsTab />,
    nested: true,
  },
  {
    path: "index",
    label: "Index",
    icon: Activity,
    element: <HubIndexTab />,
    nested: true,
  },
];
